/**
 * News administration types. These mirror the Pydantic schemas in
 * `api/api/schemas/news_admin.py` — field names stay snake_case on both sides so
 * there is no translation layer to drift.
 */

/**
 * The two closed vocabularies of the content taxonomy, mirroring
 * `api/api/schemas/content.py`.
 *
 * `ContentFormat` is what a piece of content *is*: one value, and the one that
 * decides which player the member app renders. `ContentTopic` is what it is *about*:
 * one to three values. Neither is free-form — the panel offers the list and nothing
 * else, so the member app's two selectors can be a fixed, legible grid.
 *
 * Slugs stay Czech on the wire. They are identity, not copy, and are labelled per
 * language in the `formats.*` and `topics.*` catalogues.
 */
export type ContentFormat = "clanek" | "video" | "protokol" | "zaznam-webinare";

export const CONTENT_FORMATS: ContentFormat[] = [
  "clanek",
  "video",
  "protokol",
  "zaznam-webinare",
];

export type ContentTopic =
  | "energie"
  | "soustredeni"
  | "regenerace-spanek"
  | "metabolismus"
  | "imunita"
  | "vykon-sport"
  | "suplementace"
  | "novinky-z-elyxeeru";

export const CONTENT_TOPICS: ContentTopic[] = [
  "energie",
  "soustredeni",
  "regenerace-spanek",
  "metabolismus",
  "imunita",
  "vykon-sport",
  "suplementace",
  "novinky-z-elyxeeru",
];

/** The API rejects anything outside these bounds; the form enforces them first. */
export const MIN_CONTENT_TOPICS = 1;
export const MAX_CONTENT_TOPICS = 3;

/** Mirrors `MAX_GALLERY_IMAGES` in `api/api/schemas/news_admin.py`. */
export const MAX_GALLERY_IMAGES = 10;

export interface NewsArticleAdminSummary {
  id: string;
  slug: string;
  locale: string;
  title: string;
  excerpt: string | null;
  cover_image_url: string | null;
  topics: ContentTopic[];
  format: ContentFormat;
  published_at: string;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

export interface NewsArticleAdminDetail extends NewsArticleAdminSummary {
  /** Markdown, exactly as the member app will render it. */
  body: string;
  /** Up to ten further photographs the member browses in the cover's place, in order. */
  gallery_image_urls: string[];
  keywords: string[];
  /** Plain scientific citations, rendered after the Markdown body. */
  references: string[];
  /** A bare YouTube id, never a URL. Null unless the article carries a video. */
  video_youtube_id: string | null;
  video_duration_seconds: number | null;
}

export interface NewsArticleAdminPage {
  items: NewsArticleAdminSummary[];
  total: number;
  limit: number;
  offset: number;
}

/** Allow-listed server-side ordering exposed by `GET /admin/news`. */
export type NewsArticleSort =
  "title" | "locale" | "status" | "format" | "published_at" | "updated_at";

export type SortOrder = "asc" | "desc";

export interface NewsArticleCreate {
  slug: string;
  locale: string;
  title: string;
  excerpt: string | null;
  body: string;
  /** One to three, from the closed vocabulary. Required — the API 422s on an empty list. */
  topics: ContentTopic[];
  keywords: string[];
  references: string[];
  cover_image_url: string | null;
  /** Ordered, distinct, at most {@link MAX_GALLERY_IMAGES}. */
  gallery_image_urls: string[];
  format: ContentFormat;
  /** A bare eleven-character YouTube id, never a watch URL. */
  video_youtube_id: string | null;
  video_duration_seconds: number | null;
  /** Omit (null) to publish as of now. */
  published_at: string | null;
  is_published: boolean;
}

/** Every field optional; the API keeps whatever is omitted. */
export type NewsArticleUpdate = Partial<NewsArticleCreate>;
